'use client';

import { Box, TypographyProps } from '@mui/material';
import { DigitalhubDivider, HubIcon, OpenFolderIcon, WhiteButton } from '../../../components';
import { useTranslations } from 'next-intl';
import { useBreakpoint } from '../../../hooks';
import {
    BodyLarge,
    BodyMedium,
    Center,
    Column,
    DisplayExtraLarge,
    DisplayLarge,
    DisplayMedium,
    HeadlineSmall,
    Row
} from '../../../styles';
import { getBreakpointValue, isCompact, isExpanded, isMedium } from '../../../types';
import { Banner } from './styles';
import { ROUTES } from '../../../../constants';

const LandingPageUpperSection = () => {
    const t = useTranslations('landing_page');
    const breakpoint = useBreakpoint();

    const isCompactLayout = isCompact(breakpoint);
    const isMediumLayout = isMedium(breakpoint);
    const isExpandedLayout = isExpanded(breakpoint);

    const renderText = (text: string, props: TypographyProps) => {
        if (isExpandedLayout) {
            return (
                <HeadlineSmall color='white' {...props}>
                    {text}
                </HeadlineSmall>
            );
        } else if (isMediumLayout) {
            return (
                <BodyLarge color='white' {...props}>
                    {text}
                </BodyLarge>
            );
        } else if (isCompactLayout) {
            return (
                <BodyMedium color='white' {...props}>
                    {text}
                </BodyMedium>
            );
        }
    };

    const renderGridText = (text: string, props?: TypographyProps) => {
        if (isExpandedLayout) {
            return (
                <DisplayExtraLarge px={4} py={6} {...props} sx={{ wordBreak: 'break-word' }}>
                    {text}
                </DisplayExtraLarge>
            );
        } else if (isMediumLayout) {
            return (
                <DisplayLarge px={2} py={3} {...props}>
                    {text}
                </DisplayLarge>
            );
        } else if (isCompactLayout) {
            return (
                <DisplayMedium px={1} py={2} {...props}>
                    {text}
                </DisplayMedium>
            );
        }
    };

    const renderUpperPart = () => (
        <Row>
            <Center>{renderGridText(t('banner_title_1'), { color: 'white' })}</Center>
            {isExpandedLayout && (
                <>
                    <DigitalhubDivider orientation='vertical' isWhite />
                    <HubIcon style={{ margin: 32 }} />
                </>
            )}
        </Row>
    );

    const renderSignInTile = () => (
        <Column
            p={getBreakpointValue(breakpoint, 2, 4, 6)}
            gap={3}
            flexShrink={1}
            maxWidth={'40%'}
            height={'100%'}
            justifyContent={'center'}
        >
            {renderText(t('banner_text_1'), { maxWidth: 500 })}
            <WhiteButton label={t('sign_in')} sx={{ width: 'fit-content' }} href={ROUTES.Login} />
        </Column>
    );

    const renderFolderTile = () => (
        <Column height={'100%'} width={'100%'}>
            <Center display='flex' height={'100%'}>
                {renderGridText(t('banner_title_2'), { alignSelf: 'center', color: 'white' })}
            </Center>
            <DigitalhubDivider isWhite />
            <Row height={'100%'}>
                {!isCompactLayout && (
                    <>
                        <Box>
                            <OpenFolderIcon />
                        </Box>
                        <DigitalhubDivider orientation='vertical' isWhite />
                    </>
                )}
                {renderText(t('banner_text_1'), { p: 2 })}
            </Row>
        </Column>
    );

    const renderLowerPart = () => {
        if (isExpandedLayout) {
            return (
                <Row>
                    {renderSignInTile()}
                    <DigitalhubDivider orientation='vertical' isWhite />
                    {renderFolderTile()}
                </Row>
            );
        } else
            return (
                <Column>
                    {renderSignInTile()}
                    <DigitalhubDivider isWhite />
                    {renderFolderTile()}
                </Column>
            );
    };
    return (
        <Banner mb={8}>
            {renderUpperPart()}
            <DigitalhubDivider isWhite />
            {renderLowerPart()}
        </Banner>
    );
};

export { LandingPageUpperSection };
